import * as GC from '@mescius/spread-sheets';

import { LoopRange as CollectedLoopRange } from '../services/api/generated';
import {
  CollectedProperty,
  CellTag,
  Loop,
  LoopId,
  ExtentionLoopMappingProperty,
} from '../types';
import {
  addLoopEndToCellTag,
  addLoopHeaderEndToCellTag,
  addLoopStartToCellTag,
  removeLoopFromCellTag,
  updateCellTagSafely,
} from './cellTag';
import {
  applyLoopBackground,
  applyLoopHeaderBackground,
  clearBackgroundsInLoop,
} from './style';
import { collectProperties, removePropertyByCell } from './property';
import {
  isIntersectingRange,
  normalizeRange,
  getApiRangeEnd,
  toApiRangeAddress,
} from './range';
import { MAPPING_RESTRICTION, OPTION_CATEGORY_ID } from '../constants';

export type LoopRange = {
  loopId: LoopId;
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
  headerEndRow: number;
  headerEndCol: number;
};

/**
 *
 * @param sheet 対象のワークシート
 * @returns ループ範囲の配列
 */
export function collectLoopRanges(
  sheet: GC.Spread.Sheets.Worksheet
): LoopRange[] {
  const usedRange = sheet.getUsedRange(GC.Spread.Sheets.UsedRangeType.tag);
  if (!usedRange) return [];

  const partialMap = new Map<
    number,
    {
      startRow?: number;
      startCol?: number;
      endRow?: number;
      endCol?: number;
      headerEndRow?: number;
      headerEndCol?: number;
    }
  >();

  const rowEnd = usedRange.row + usedRange.rowCount;
  const colEnd = usedRange.col + usedRange.colCount;

  for (let row = usedRange.row; row < rowEnd; row++) {
    for (let col = usedRange.col; col < colEnd; col++) {
      const tag = sheet.getTag(row, col) as CellTag | undefined;
      if (!tag?.loop) continue;

      if (tag.loop.start !== undefined) {
        const current = partialMap.get(tag.loop.start) ?? {};
        partialMap.set(tag.loop.start, {
          ...current,
          startRow: row,
          startCol: col,
        });
      }

      if (tag.loop.end !== undefined) {
        const current = partialMap.get(tag.loop.end) ?? {};
        partialMap.set(tag.loop.end, {
          ...current,
          endRow: row,
          endCol: col,
        });
      }

      if (tag.loop.headerEnd !== undefined) {
        const current = partialMap.get(tag.loop.headerEnd) ?? {};
        partialMap.set(tag.loop.headerEnd, {
          ...current,
          headerEndRow: row,
          headerEndCol: col,
        });
      }
    }
  }

  const loops: LoopRange[] = [];

  for (const [loopId, partial] of partialMap.entries()) {
    if (
      partial.startRow === undefined ||
      partial.startCol === undefined ||
      partial.endRow === undefined ||
      partial.endCol === undefined
      // TODO: データ不整合を考慮（明細ヘッダー行範囲がAPIから返らない）
      // partial.headerEndRow === undefined ||
      // partial.headerEndCol === undefined
    ) {
      continue;
    }

    loops.push({
      loopId,
      startRow: partial.startRow,
      startCol: partial.startCol,
      endRow: partial.endRow,
      endCol: partial.endCol,
      // TODO: データ不整合を考慮（明細ヘッダー行範囲がAPIから返らない）
      headerEndRow: partial.headerEndRow ?? partial.endRow,
      headerEndCol: partial.headerEndCol ?? partial.endCol,
    });
  }

  return loops;
}

/**
 *
 * @param sheet 対象のワークシート
 * @param row 対象の行番号
 * @param col 対象の列番号
 * @returns ループ範囲を返す。ループ範囲が存在しない場合はnullを返す
 */
export function findContainingLoop(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): LoopRange | null {
  const loops = collectLoopRanges(sheet);

  return (
    loops.find(
      (loop) =>
        row >= loop.startRow &&
        row <= loop.endRow &&
        col >= loop.startCol &&
        col <= loop.endCol
    ) ?? null
  );
}

/**
 *
 * @param sheet 対象のワークシート
 * @param row 対象の行番号
 * @param col 対象の列番号
 * @returns
 */
export function isCellInAnyLoop(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): boolean {
  return findContainingLoop(sheet, row, col) !== null;
}

/**
 *
 * @param sheet 対象のワークシート
 * @param loopId 対象のループID
 * @returns
 */
export function findLoopById(
  sheet: GC.Spread.Sheets.Worksheet,
  loopId: LoopId
): LoopRange | null {
  return (
    collectLoopRanges(sheet).find((loop) => loop.loopId === loopId) ?? null
  );
}

/**
 *
 * @param row 対象の行番号
 * @param col 対象の列番号
 * @param loop 対象のループ範囲
 * @returns ループヘッダー内にある場合はtrue、そうでない場合はfalse
 */
export function isCellInLoopHeader(
  row: number,
  col: number,
  loop: LoopRange
): boolean {
  if (!loop.headerEndCol || !loop.headerEndRow) return false;
  return (
    row >= loop.startRow &&
    row <= loop.headerEndRow &&
    col >= loop.startCol &&
    col <= loop.headerEndCol
  );
}

/**
 * セルがループ外またはループヘッダー内の場合にtrueを返す
 * @param sheet 対象のワークシート
 * @param row 対象の行番号
 * @param col 対象の列番号
 * @returns
 */
export function isCellOutsideLoopOrInLoopHeader(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): boolean {
  const loop = findContainingLoop(sheet, row, col);

  if (!loop) {
    return true;
  }

  return isCellInLoopHeader(row, col, loop);
}

/**
 *
 * @param loop
 * @returns
 */
export function toSheetRange(loop: LoopRange): GC.Spread.Sheets.Range {
  return new GC.Spread.Sheets.Range(
    loop.startRow,
    loop.startCol,
    loop.endRow - loop.startRow + 1,
    loop.endCol - loop.startCol + 1
  );
}

/**
 *
 * @param sheet 対象のワークシート
 * @param targetRange 対象の範囲
 * @returns
 */
export function isRangeIntersectingAnyLoop(
  sheet: GC.Spread.Sheets.Worksheet,
  targetRange: GC.Spread.Sheets.Range
): boolean {
  const loops = collectLoopRanges(sheet);
  const normalizedRange = normalizeRange(sheet, targetRange);

  return loops.some((loop) =>
    isIntersectingRange(normalizedRange, toSheetRange(loop))
  );
}

/**
 * 特定の行に干渉する明細範囲を返す関数
 * 行の挿入・削除イベントなどで使用
 * @param sheet 対象のワークシート
 * @param row 対象の行番号
 * @param rowCount 対象の行数
 * @returns
 */
export function findLoopsIntersectingRows(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  rowCount: number
): LoopRange[] {
  const targetStartRow = row;
  const targetEndRow = row + rowCount - 1;

  return collectLoopRanges(sheet).filter(
    (loop) => targetStartRow <= loop.endRow && loop.startRow <= targetEndRow
  );
}

/**
 * 特定の列に干渉する明細範囲を返す関数
 * 列の削除イベントなどで使用
 * @param sheet 対象のワークシート
 * @param col 対象の列番号
 * @param colCount 対象の列数
 * @returns
 */
export function findLoopsIntersectingColumns(
  sheet: GC.Spread.Sheets.Worksheet,
  col: number,
  colCount: number
): LoopRange[] {
  const targetStartCol = col;
  const targetEndCol = col + colCount - 1;

  return collectLoopRanges(sheet).filter(
    (loop) => targetStartCol <= loop.endCol && loop.startCol <= targetEndCol
  );
}

/**
 * 特定の行レンジに開始・終了点が含まれる明細範囲を返す関数
 * 列の削除イベントなどで使用
 * @param sheet 対象のワークシート
 * @param row 対象の行番号
 * @param rowCount 対象の行数
 * @returns
 */
export function findLoopsWhoseAnchorsAreInColumns(
  sheet: GC.Spread.Sheets.Worksheet,
  col: number,
  colCount: number
): LoopRange[] {
  const startCol = col;
  const endCol = col + colCount - 1;

  return collectLoopRanges(sheet).filter((loop) => {
    const includesLoopStartCol =
      startCol <= loop.startCol && loop.startCol <= endCol;

    const includesLoopEndCol = startCol <= loop.endCol && loop.endCol <= endCol;

    return includesLoopStartCol || includesLoopEndCol;
  });
}

export type CellShiftOperation =
  | 'insertDown'
  | 'insertRight'
  | 'deleteUp'
  | 'deleteLeft';

/**
 * セルの挿入・削除で影響を受ける明細範囲を取得
 * @param sheet 対象のワークシート
 * @param range 対象の範囲
 * @param operation セルの挿入・削除操作
 * @returns
 */
export function findLoopsAffectedByCellShift(
  sheet: GC.Spread.Sheets.Worksheet,
  range: GC.Spread.Sheets.Range,
  operation: CellShiftOperation
): LoopRange[] {
  const rangeStartRow = range.row;
  const rangeEndRow = range.row + range.rowCount - 1;
  const rangeStartCol = range.col;
  const rangeEndCol = range.col + range.colCount - 1;

  return collectLoopRanges(sheet).filter((loop) => {
    const intersectsLoop =
      rangeStartRow <= loop.endRow &&
      loop.startRow <= rangeEndRow &&
      rangeStartCol <= loop.endCol &&
      loop.startCol <= rangeEndCol;

    if (intersectsLoop) {
      return true;
    }

    const overlapsLoopRows =
      rangeStartRow <= loop.endRow && loop.startRow <= rangeEndRow;

    const overlapsLoopCols =
      rangeStartCol <= loop.endCol && loop.startCol <= rangeEndCol;

    switch (operation) {
      case 'insertDown':
      case 'deleteUp':
        return overlapsLoopCols && rangeStartRow <= loop.endRow;

      case 'insertRight':
      case 'deleteLeft':
        return overlapsLoopRows && rangeStartCol <= loop.endCol;

      default:
        return false;
    }
  });
}

/**
 * 特定のレンジに干渉する明細範囲を取得する
 * @param sheet 対象のワークシート
 * @param range 対象の範囲
 * @returns
 */
export function findLoopsIntersectingRange(
  sheet: GC.Spread.Sheets.Worksheet,
  range: GC.Spread.Sheets.Range
): LoopRange[] {
  const rangeStartRow = range.row;
  const rangeEndRow = range.row + range.rowCount - 1;
  const rangeStartCol = range.col;
  const rangeEndCol = range.col + range.colCount - 1;

  return collectLoopRanges(sheet).filter(
    (loop) =>
      rangeStartRow <= loop.endRow &&
      loop.startRow <= rangeEndRow &&
      rangeStartCol <= loop.endCol &&
      loop.startCol <= rangeEndCol
  );
}

/**
 * 明細範囲の追加
 * @param sheet 対象のワークシート
 * @param loop 追加する明細範囲
 * @returns
 */
export function addLoop(
  sheet: GC.Spread.Sheets.Worksheet,
  loop: LoopRange
): void {
  sheet.suspendPaint();

  try {
    updateCellTagSafely(sheet, loop.startRow, loop.startCol, (prev) =>
      addLoopStartToCellTag(prev, loop.loopId)
    );

    updateCellTagSafely(sheet, loop.endRow, loop.endCol, (prev) =>
      addLoopEndToCellTag(prev, loop.loopId)
    );

    updateCellTagSafely(sheet, loop.headerEndRow, loop.headerEndCol, (prev) =>
      addLoopHeaderEndToCellTag(prev, loop.loopId)
    );

    applyLoopBackground(sheet, loop);
    applyLoopHeaderBackground(sheet, loop);
  } catch (error: unknown) {
    throw new Error(error as string);
  } finally {
    sheet.resumePaint();
  }
}

/**
 * idを指定して明細範囲を削除する
 * 削除対象:
 * - 明細範囲
 * - 明細範囲内のプロパティ
 * - 背景色
 * @param sheet 対象のワークシート
 * @param loopId 削除する明細範囲のID
 * @returns
 */
export function removeLoopById(
  sheet: GC.Spread.Sheets.Worksheet,
  loopId: LoopId
): void {
  const loop = findLoopById(sheet, loopId);
  if (!loop) return;

  sheet.suspendPaint();

  try {
    for (let row = loop.startRow; row <= loop.endRow; row++) {
      for (let col = loop.startCol; col <= loop.endCol; col++) {
        removePropertyByCell(sheet, row, col);
      }
    }

    updateCellTagSafely(sheet, loop.startRow, loop.startCol, (prev) =>
      removeLoopFromCellTag(prev, loopId)
    );

    updateCellTagSafely(sheet, loop.endRow, loop.endCol, (prev) =>
      removeLoopFromCellTag(prev, loopId)
    );

    // 背景色の削除
    clearBackgroundsInLoop(sheet, loop);
  } finally {
    sheet.resumePaint();
  }
}

/**
 * シート内で一意の明細範囲IDを生成
 * カウンターはシートのタグに持つ
 * @param sheet 対象のワークシート
 * @returns
 */
export function createLoopId(sheet: GC.Spread.Sheets.Worksheet): LoopId {
  const sheetTag = (sheet.tag() ?? {}) as { loopCounter?: number };

  const nextLoopCounter = (sheetTag.loopCounter ?? 0) + 1;

  sheet.tag({
    ...sheetTag,
    loopCounter: nextLoopCounter,
  });

  return nextLoopCounter;
}

/**
 *
 * @param sheet
 * @param initialLoops
 */
export function initializeLoopIdCounter(
  sheet: GC.Spread.Sheets.Worksheet,
  initialLoops: Loop[]
) {
  const maxId = Math.max(...initialLoops.map((loop) => loop.loopId), 0);
  const sheetTag = (sheet.tag() ?? {}) as {
    loopCounter?: number;
  };
  sheet.tag({
    ...sheetTag,
    loopCounter: maxId,
  });
}

/**
 *
 * @param loops
 * @returns
 */
export function collectUniqueLoops(loops: LoopRange[]): LoopRange[] {
  const loopMap = new Map<number, LoopRange>();

  for (const loop of loops) {
    loopMap.set(loop.loopId, loop);
  }

  return [...loopMap.values()];
}

/**
 *
 * @param sheet
 * @param loop
 * @returns
 */
export function toApiLoopRange(
  sheet: GC.Spread.Sheets.Worksheet,
  loop: LoopRange
): CollectedLoopRange {
  const normalizedEnd = getApiRangeEnd(sheet, loop.endRow, loop.endCol);

  return {
    startRowIndex: loop.startRow,
    endRowIndex: normalizedEnd.endRow,
    startColIndex: loop.startCol,
    endColIndex: normalizedEnd.endCol,
    loopRangeTargetCell: toApiRangeAddress(
      sheet,
      loop.startRow,
      loop.startCol,
      loop.endRow,
      loop.endCol
    ),
  };
}

/**
 *
 * @param sheet
 * @param loop
 * @returns
 */
export function getLoopHeaderRange(
  sheet: GC.Spread.Sheets.Worksheet,
  loop: LoopRange
): string {
  return toApiRangeAddress(
    sheet,
    loop.startRow,
    loop.startCol,
    loop.headerEndRow,
    loop.headerEndCol
  );
}

/**
 *
 * @param sheet
 * @param loop
 * @returns
 */
export function getLoopCount(
  sheet: GC.Spread.Sheets.Worksheet,
  loop: LoopRange
): number {
  const normalizedLoopEnd = getApiRangeEnd(sheet, loop.endRow, loop.endCol);
  const normalizedHeaderEnd = getApiRangeEnd(
    sheet,
    loop.headerEndRow,
    loop.headerEndCol
  );

  const loopRowCount = normalizedLoopEnd.endRow - loop.startRow + 1;
  const headerRowCount = normalizedHeaderEnd.endRow - loop.startRow + 1;

  return Math.floor(loopRowCount / headerRowCount);
}

/**
 *
 * @param property
 * @param loop
 * @returns
 */
export function isPropertyInLoop(
  property: CollectedProperty,
  loop: LoopRange
): boolean {
  return (
    property.rowIndex >= loop.startRow &&
    property.rowIndex <= loop.endRow &&
    property.colIndex >= loop.startCol &&
    property.colIndex <= loop.endCol
  );
}

/**
 *
 * @param property
 * @param loop
 * @returns
 */
export function isPropertyInLoopHeader(
  property: CollectedProperty,
  loop: LoopRange
): boolean {
  return (
    property.rowIndex >= loop.startRow &&
    property.rowIndex <= loop.headerEndRow &&
    property.colIndex >= loop.startCol &&
    property.colIndex <= loop.headerEndCol
  );
}

/**
 * 対象のセルがどのループ範囲内にあるかを返す関数
 * @param sheet
 * @param row
 * @param col
 * @returns
 */
export function findContainingLoopMappingProperties(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): ExtentionLoopMappingProperty[] {
  const loops = collectLoopRanges(sheet);
  const properties = collectProperties(sheet);
  const containingLoop = findContainingLoop(sheet, row, col);
  const loopMappingProperties: ExtentionLoopMappingProperty[] = loops
    .map((loop) => {
      const mappingProperties = properties.filter(
        (property) =>
          isPropertyInLoop(property, loop) &&
          isPropertyInLoopHeader(property, loop)
      );

      let categoryId = 0;
      let mappingRestriction = '';
      const sameCategoryMappingProperties = mappingProperties.filter(
        (property) =>
          property.mappingRestriction !== MAPPING_RESTRICTION.NOTHING &&
          property.categoryId !== OPTION_CATEGORY_ID
      );

      // 同一ループ内のプロパティのうち、『制限なし』と『オプションカテゴリ』を除けばカテゴリは一意に定まるはず
      if (sameCategoryMappingProperties.length > 0) {
        categoryId = sameCategoryMappingProperties[0].categoryId;
        mappingRestriction =
          sameCategoryMappingProperties[0].mappingRestriction;
      }

      return {
        loopId: loop.loopId,
        loopCount: getLoopCount(sheet, loop),
        loopRange: toApiLoopRange(sheet, loop),
        mappingEnableCellRange: getLoopHeaderRange(sheet, loop),
        mappingProperties,
        categoryId: categoryId,
        mappingRestriction: mappingRestriction,
      };
    })
    .filter((loop) => loop.loopId === containingLoop?.loopId);
  return loopMappingProperties;
}
